import type CardClient from '@atlaskit/link-provider/client';
import type { CardStore } from '@atlaskit/linking-common/store';

import type { DependencyHealthReporter } from '../dependencyHealthAnalytics.ts';

import { createLinkDeduper } from './createLinkDeduper.ts';
import { resolveSmartLinkByUrl } from './resolveSmartLinkByUrl.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

/**
 * Hydrates the URLs the graph returned for a page of incoming links into
 * `EnhancedSmartLink`s, through the same store-then-ORS resolution the outgoing
 * links use (`resolveSmartLinkByUrl`). The graph supplies nothing but the url; all
 * display metadata comes from the resolved response.
 *
 * Unresolved URLs are dropped rather than rendered bare: unlike an outgoing link,
 * whose url is on the page in front of the user, an inbound row with no title says
 * nothing. Access-restricted and unauthorized entities still resolve — they carry
 * an `access` status and their provider's connect prompt — so they are kept.
 *
 * Links are de-duplicated by identity (see `createLinkDeduper`): the resolved `ari`,
 * or the url without its query string and hash when ORS gave us none. The urls are
 * already unique, so this only collapses rows whose different urls name the same
 * entity (e.g. a comment whose url ORS resolves to its parent page, or the same page
 * with a different timestamp param). Order follows the incoming url order.
 */
export async function hydrateIncomingLinks(
	urls: string[],
	store: { getState: () => CardStore },
	cardClient: CardClient,
	reportHealth?: DependencyHealthReporter,
): Promise<EnhancedSmartLink[]> {
	const resolved = await Promise.all(
		urls.map((url, index) => resolveSmartLinkByUrl(url, index, store, cardClient, reportHealth)),
	);

	const links: EnhancedSmartLink[] = [];
	const isDuplicate = createLinkDeduper();

	resolved.forEach(({ link, isResolved }) => {
		if (!isResolved || isDuplicate(link)) {
			return;
		}
		links.push(link);
	});

	return links;
}
